import { Box, Typography, TextField, InputAdornment } from "@mui/material";
import React, { useState } from "react";
import { useFiltersWithContext } from "../filter-context/useFiltersWithContext";
import { useTranslation } from "../../../hooks/useTranslation";

const MAX_INT = 999999999; // 9007199254740991

type IntegerRangeFilterProps = {
  name: string;
  labelKey: string;
  labelVars?: Record<string, string>;
  adornment?: React.ReactNode;
  labelSuffix?: string;
};

export const IntegerRangeFilter = (props: IntegerRangeFilterProps) => {
  const { name, labelKey, labelVars, adornment, labelSuffix } = props;

  const { getFilterValue, setFilter, deleteFilter } = useFiltersWithContext();
  const { translate } = useTranslation();

  const [minValue, setMinValue] = useState<string>(
    getFilterValue(name, "gte")?.toString() ?? "",
  );
  const [maxValue, setMaxValue] = useState<string>(
    getFilterValue(name, "lte")?.toString() ?? "",
  );
  const [error, setError] = useState(false);

  const sanitizeValue = (value: string) => {
    const num = Number.parseInt(value);
    if (Number.isNaN(num)) return "";
    return Math.min(num, MAX_INT).toString();
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    type: "min" | "max",
  ) => {
    const raw = e.target.value;
    const sanitized = sanitizeValue(raw);

    if (type === "min") {
      setMinValue(sanitized);
    } else {
      setMaxValue(sanitized);
    }

    const min = type === "min" ? sanitized : minValue;
    const max = type === "max" ? sanitized : maxValue;

    if (min === "" || max === "" || parseInt(min) <= parseInt(max)) {
      setError(false);
      handleRangeChange(parseInt(min), "gte");
      handleRangeChange(parseInt(max), "lte");
    } else {
      setError(true);
    }
  };

  const handleRangeChange = (value: number | null, operator: "gte" | "lte") => {
    if (value !== null && !isNaN(value)) {
      setFilter({
        field: name,
        value: Math.min(value, MAX_INT),
        labelInfo: {
          descriptionKey: labelKey,
          descriptionSuffixKey: "filters.operators." + operator,
          value: value.toString() + (labelSuffix ?? ""),
        },
        operator: operator,
      });
    } else {
      deleteFilter(name, operator);
    }
  };

  return (
    <Box display="flex" flexDirection="row" gap={2} alignItems="center">
      <Box flex={1}>
        <Typography>{translate(labelKey, labelVars)}</Typography>
        <Typography variant="body2" color="error">
          {error && translate("common.errors.maxValueLessThanMinValue")}
        </Typography>
      </Box>

      <TextField
        size="small"
        label={translate("filters.placeholders.min")}
        type="number"
        value={minValue}
        onChange={(e) => handleChange(e, "min")}
        sx={{ width: 200 }}
        error={error}
        slotProps={{
          htmlInput: {
            max: MAX_INT,
          },
          input: {
            startAdornment: adornment && (
              <InputAdornment position="start">{adornment}</InputAdornment>
            ),
          },
        }}
      />

      <TextField
        size="small"
        label={translate("filters.placeholders.max")}
        type="number"
        value={maxValue}
        onChange={(e) => handleChange(e, "max")}
        sx={{ width: 200 }}
        error={error}
        slotProps={{
          htmlInput: {
            max: MAX_INT,
          },
          input: {
            startAdornment: adornment && (
              <InputAdornment position="start">{adornment}</InputAdornment>
            ),
          },
        }}
      />
    </Box>
  );
};
